import React, { createContext, useContext, useEffect, useState } from 'react';
import syncFeatureFlags, { FeatureFlagInterface, featureFlagList } from './components/constants';
import { useLocalStorageState } from './utils/hooks/useLocalStorageState';
import { Page } from './models/Page';

interface AppContextType {
  agent: connect.Agent | undefined;
  setAgent: React.Dispatch<React.SetStateAction<connect.Agent | undefined>>;

  activePage: Page;
  setActivePage: React.Dispatch<React.SetStateAction<Page>>;

  featureFlags: FeatureFlagInterface[] | [];
  setFeatureFlags: React.Dispatch<React.SetStateAction<FeatureFlagInterface[]>>;

  agentState: connect.AgentState | null;
  setAgentState: React.Dispatch<React.SetStateAction<connect.AgentState | null>>;
}

const AppContext = createContext<AppContextType>({
  agent: undefined,
  setAgent: () => {},
  activePage: Page.TELEPHONY,
  setActivePage: () => {},
  featureFlags: [],
  setFeatureFlags: () => {},
  agentState: null,
  setAgentState: () => {}
});

export const useAppContext = (): AppContextType => useContext(AppContext);

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [agent, setAgent] = useState<connect.Agent>();
  const [activePage, setActivePage] = useState<Page>(Page.TELEPHONY);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagInterface[] | []>([]);
  const [agentState, setAgentState] = useState<connect.AgentState | null>(null);

  const [localFeatureFlags, setLocalFeatureFlags] = useLocalStorageState<FeatureFlagInterface[]>(
    'featureFlags',
    featureFlagList
  );
  useEffect(() => {
    setFeatureFlags(localFeatureFlags);
  }, [localFeatureFlags]);

  useEffect(() => {
    const synchronisedFeatureFlags = syncFeatureFlags(localFeatureFlags);
    if (JSON.stringify(localFeatureFlags) !== JSON.stringify(synchronisedFeatureFlags)) {
      setLocalFeatureFlags(synchronisedFeatureFlags);
    }
  }, [localFeatureFlags, setLocalFeatureFlags]);

  return (
    <AppContext.Provider
      value={{
        agent,
        setAgent,
        activePage,
        setActivePage,
        featureFlags,
        agentState,
        setAgentState,
        setFeatureFlags: setLocalFeatureFlags
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
